// LA FILE DES CHRONOS (mode hors ligne, option B du 25/07).
//
// Ce qu'on évite : « j'ai fait mon meilleur temps au chalet et il n'a pas compté ». Les temps
// réalisés sans réseau sont rangés dans le navigateur, puis envoyés au retour.
//
// Les règles qui rendent la chose sûre, dans l'ordre :
//
//   1. ON N'ENVOIE QUE DES CHRONOS. Ni compteurs de quêtes, ni pièces : le serveur est la seule
//      autorité sur l'économie et doit le rester. Un temps est une donnée idempotente — le serveur
//      ne garde que le meilleur (`WHERE excluded.best_ms < scores.best_ms`), donc réenvoyer deux
//      fois le même n'a AUCUN effet la seconde fois. C'est ce qui rend la file rejouable sans
//      jamais créditer deux fois.
//
//   2. UN SEUL TEMPS PAR CARTE ET PAR MODE. Vingt essais hors ligne ne font pas vingt entrées : on
//      garde le MEILLEUR. La file reste minuscule, et la sémantique est exactement celle du serveur
//      (records, duels et défis se jouent tous sur le meilleur temps).
//
//   3. UNE ENTRÉE EMPOISONNÉE NE BLOQUE JAMAIS LA FILE. Si le serveur refuse une entrée (400 :
//      carte supprimée entre-temps), elle est JETÉE. Sans cette règle, une seule entrée invalide
//      empêcherait tous les temps suivants de partir, pour toujours.
//
// Module PUR côté logique : le stockage est injecté, donc testable sans navigateur.

/** Un temps en attente d'envoi. `at` sert seulement à purger les plus vieux si la file déborde. */
export interface QueuedScore {
  slug: string
  timeMs: number
  deaths: number
  dash: boolean
  torch: boolean
  at: number
}

/** Clé de rangement dans le stockage local. */
export const QUEUE_KEY = 'mad.scoreq'

/** Plafond de sécurité : au-delà, les plus anciens partent. Une famille ne remplira jamais ça,
 *  mais un stockage qui grossit sans borne est un bug qui attend son heure. */
export const QUEUE_MAX = 200

/** Le minimum d'un stockage à la localStorage (pour injecter un faux en test). */
export interface Storageish {
  getItem(k: string): string | null
  setItem(k: string, v: string): void
}

/** Identité d'un temps : une carte, un mode. Deux entrées de même clé ne coexistent jamais. */
const keyOf = (s: { slug: string; dash: boolean; torch: boolean }): string =>
  `${s.slug}|${s.dash ? 1 : 0}|${s.torch ? 1 : 0}`

/** Lit la file. Un contenu illisible (stockage bricolé, version antérieure) rend une file VIDE
 *  plutôt qu'une exception : perdre des temps en attente est regrettable, planter au démarrage
 *  serait pire. */
export function readQueue(store: Storageish): QueuedScore[] {
  try {
    const raw = store.getItem(QUEUE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (e): e is QueuedScore =>
        typeof e === 'object' && e !== null &&
        typeof (e as QueuedScore).slug === 'string' &&
        Number.isFinite((e as QueuedScore).timeMs) &&
        Number.isFinite((e as QueuedScore).deaths),
    )
  } catch {
    return []
  }
}

/** Écrit la file (silencieux si le stockage est plein ou refusé : navigation privée, quota). */
export function writeQueue(store: Storageish, q: readonly QueuedScore[]): void {
  try {
    store.setItem(QUEUE_KEY, JSON.stringify(q))
  } catch {
    /* quota dépassé / stockage interdit : on renonce, jamais d'exception vers le jeu */
  }
}

/**
 * Range un temps. Règle 2 : s'il existe déjà un temps pour cette carte et ce mode, on garde le
 * MEILLEUR des deux (un essai plus lent n'apporte rien, ni au record, ni à un duel, ni à un défi).
 * Renvoie la file résultante.
 */
export function enqueueScore(store: Storageish, entry: QueuedScore): QueuedScore[] {
  const q = readQueue(store)
  const k = keyOf(entry)
  const i = q.findIndex((e) => keyOf(e) === k)
  if (i >= 0) {
    if (entry.timeMs < q[i]!.timeMs) q[i] = entry
  } else {
    q.push(entry)
  }
  // Débordement : on sacrifie les plus ANCIENNES (les plus susceptibles d'être déjà périmées).
  const trimmed = q.length > QUEUE_MAX ? [...q].sort((a, b) => a.at - b.at).slice(q.length - QUEUE_MAX) : q
  writeQueue(store, trimmed)
  return trimmed
}

/** Retire une entrée (envoyée, ou définitivement refusée). */
export function dropScore(store: Storageish, entry: QueuedScore): QueuedScore[] {
  const k = keyOf(entry)
  const q = readQueue(store).filter((e) => keyOf(e) !== k)
  writeQueue(store, q)
  return q
}

/** Ce qu'un envoi peut donner, du point de vue de la file. */
export type SendOutcome = 'envoye' | 'refuse' | 'reseau'

/**
 * Décide du sort d'une entrée d'après ce qui s'est passé à l'envoi. Séparé en fonction PURE parce
 * que c'est ici qu'est la règle 3, et qu'une règle de ce genre mérite d'être testée seule :
 *   - envoyé      -> on retire l'entrée ;
 *   - refusé (4xx sauf 401) -> on retire aussi : elle ne passera JAMAIS, la garder bloque la file ;
 *   - réseau / 401 -> on garde et on s'arrête là (hors ligne, ou session à renouveler).
 */
export function outcomeFor(status: number | null): SendOutcome {
  if (status === null) return 'reseau'                      // fetch a échoué : pas de réseau
  if (status >= 200 && status < 300) return 'envoye'
  if (status === 401 || status === 429 || status >= 500) return 'reseau' // temporaire : on retentera
  return 'refuse'                                           // 400, 404... : sans espoir
}
